// Pure functions that transform TradeCaseListItem[] into chart-ready data series.
// No React, no side effects — just data in, data out.

interface TradeCaseInput {
	id: number;
	pair: string | null;
	direction: string | null;
	currentState: string;
	outcome: string | null;
	openedAt: string | null;
	closedAt: string | null;
	entryPriceFilled: string | null;
	profitLossMoney: string | null;
	profitLossPercent: string | null;
	tpHitsCount: number;
	tpTargetCount: number;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const parseNumeric = (value: string | null): number | null => {
	if (value === null || value === undefined) return null;
	const cleaned = value.replace(/[,%+\s]/g, "");
	const parsed = Number.parseFloat(cleaned);
	return Number.isFinite(parsed) ? parsed : null;
};

const isExecuted = (t: TradeCaseInput): boolean =>
	t.openedAt !== null ||
	(t.entryPriceFilled !== null && t.entryPriceFilled.trim() !== "");

const TERMINAL_STATES = new Set([
	"COMPLETED",
	"CLOSED",
	"CANCELED",
	"FAILED",
]);

const isTerminalExecuted = (t: TradeCaseInput): boolean =>
	isExecuted(t) && TERMINAL_STATES.has(t.currentState);

// ── Equity Curve (Line + Area) ───────────────────────────────────────────────

export interface EquityCurvePoint {
	/** Trade index (1-based) */
	index: number;
	/** ISO timestamp of closedAt */
	date: string;
	/** Formatted date label for the X axis */
	label: string;
	/** Running cumulative P/L */
	cumulativePL: number;
	/** Peak P/L up to this point */
	peak: number;
	/** Drawdown from peak (always <= 0) */
	drawdown: number;
	/** This trade's P/L */
	tradePL: number;
	pair: string;
}

export const buildEquityCurveData = (
	tradeCases: TradeCaseInput[],
): EquityCurvePoint[] => {
	const sorted = tradeCases
		.filter(
			(t): t is TradeCaseInput & { closedAt: string } =>
				isTerminalExecuted(t) && t.closedAt !== null,
		)
		.sort(
			(a, b) =>
				new Date(a.closedAt).getTime() - new Date(b.closedAt).getTime(),
		);

	let cumPL = 0;
	let peak = 0;
	const points: EquityCurvePoint[] = [];

	for (let i = 0; i < sorted.length; i++) {
		const trade = sorted[i];
		const pl = parseNumeric(trade.profitLossMoney);
		if (pl === null) continue;

		cumPL += pl;
		if (cumPL > peak) peak = cumPL;
		const drawdown = cumPL - peak; // always <= 0

		const d = new Date(trade.closedAt);
		points.push({
			index: points.length + 1,
			date: trade.closedAt,
			label: `${d.getMonth() + 1}/${d.getDate()} ${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`,
			cumulativePL: Number(cumPL.toFixed(2)),
			peak: Number(peak.toFixed(2)),
			drawdown: Number(drawdown.toFixed(2)),
			tradePL: Number(pl.toFixed(2)),
			pair: trade.pair ?? "Unknown",
		});
	}

	return points;
};

// ── Win / Loss / Breakeven Pie ───────────────────────────────────────────────

export interface WinLossSlice {
	name: string;
	value: number;
	color: string;
}

export const buildWinLossData = (
	tradeCases: TradeCaseInput[],
): WinLossSlice[] => {
	let wins = 0;
	let losses = 0;
	let breakeven = 0;

	for (const t of tradeCases) {
		if (!isTerminalExecuted(t)) continue;
		const pl = parseNumeric(t.profitLossPercent);
		if (pl === null) continue;
		if (pl > 0) wins++;
		else if (pl < 0) losses++;
		else breakeven++;
	}

	const slices: WinLossSlice[] = [];
	if (wins > 0) slices.push({ name: "Wins", value: wins, color: "#4ade80" });
	if (losses > 0)
		slices.push({ name: "Losses", value: losses, color: "#f87171" });
	if (breakeven > 0)
		slices.push({ name: "Breakeven", value: breakeven, color: "#94a3b8" });
	return slices;
};

// ── Outcome Distribution Pie ─────────────────────────────────────────────────

export interface OutcomeSlice {
	name: string;
	value: number;
	color: string;
}

const OUTCOME_COLORS: Record<string, string> = {
	COMPLETED: "#4ade80",
	CLOSED: "#60a5fa",
	CANCELED: "#fbbf24",
	FAILED: "#f87171",
	PENDING: "#94a3b8",
	OPEN: "#a78bfa",
};

export const buildOutcomeData = (
	tradeCases: TradeCaseInput[],
): OutcomeSlice[] => {
	const counts = new Map<string, number>();
	for (const t of tradeCases) {
		const state = t.currentState;
		counts.set(state, (counts.get(state) ?? 0) + 1);
	}

	return Array.from(counts.entries())
		.map(([name, value]) => ({
			name,
			value,
			color: OUTCOME_COLORS[name] ?? "#94a3b8",
		}))
		.sort((a, b) => b.value - a.value);
};

// ── TP Hit Rate Bar ──────────────────────────────────────────────────────────

export interface TpHitRateBar {
	level: string;
	hitRate: number;
	hitRatePercent: number;
}

export const buildTpHitRateData = (
	tradeCases: TradeCaseInput[],
): TpHitRateBar[] => {
	const executed = tradeCases.filter(
		(t) => isExecuted(t) && t.tpTargetCount > 0,
	);
	if (executed.length === 0) return [];

	const maxTpTarget = executed.reduce(
		(max, t) => Math.max(max, t.tpTargetCount),
		0,
	);

	return Array.from({ length: maxTpTarget }, (_, i) => {
		const level = i + 1;
		const hits = executed.filter((t) => t.tpHitsCount >= level).length;
		const rate = hits / executed.length;
		return {
			level: `TP${level}`,
			hitRate: Number(rate.toFixed(3)),
			hitRatePercent: Number((rate * 100).toFixed(1)),
		};
	});
};

// ── Pair Performance Bar ─────────────────────────────────────────────────────

export interface PairPerformanceBar {
	pair: string;
	netPL: number;
	trades: number;
	wins: number;
	losses: number;
}

export const buildPairPerformanceData = (
	tradeCases: TradeCaseInput[],
): PairPerformanceBar[] => {
	const pairMap = new Map<
		string,
		{ netPL: number; trades: number; wins: number; losses: number }
	>();

	for (const t of tradeCases) {
		if (!isTerminalExecuted(t)) continue;
		const pl = parseNumeric(t.profitLossMoney);
		if (pl === null) continue;
		const pair = t.pair ?? "Unknown";

		const entry = pairMap.get(pair) ?? {
			netPL: 0,
			trades: 0,
			wins: 0,
			losses: 0,
		};
		entry.netPL += pl;
		entry.trades++;
		if (pl > 0) entry.wins++;
		else if (pl < 0) entry.losses++;
		pairMap.set(pair, entry);
	}

	return Array.from(pairMap.entries())
		.map(([pair, data]) => ({
			pair,
			netPL: Number(data.netPL.toFixed(2)),
			trades: data.trades,
			wins: data.wins,
			losses: data.losses,
		}))
		.sort((a, b) => b.netPL - a.netPL);
};

// ── P/L Distribution Histogram ───────────────────────────────────────────────

export interface PLDistributionBucket {
	/** Label for the bucket range, e.g. "-60 to -40" */
	range: string;
	/** Number of trades falling into this bucket */
	count: number;
	/** Bucket midpoint for coloring (negative = red, positive = green) */
	midpoint: number;
}

export const buildPLDistributionData = (
	tradeCases: TradeCaseInput[],
): PLDistributionBucket[] => {
	const values: number[] = [];
	for (const t of tradeCases) {
		if (!isTerminalExecuted(t)) continue;
		const pl = parseNumeric(t.profitLossMoney);
		if (pl !== null) values.push(pl);
	}

	if (values.length < 2) return [];

	const min = Math.min(...values);
	const max = Math.max(...values);
	const range = max - min;
	if (range === 0) return [{ range: `${min.toFixed(0)}`, count: values.length, midpoint: min }];

	// Aim for 8-12 buckets
	const bucketCount = Math.min(12, Math.max(5, Math.ceil(Math.sqrt(values.length))));
	const bucketSize = range / bucketCount;

	const buckets: PLDistributionBucket[] = [];
	for (let i = 0; i < bucketCount; i++) {
		const low = min + i * bucketSize;
		const high = low + bucketSize;
		const count = values.filter(
			(v) => (i === bucketCount - 1 ? v >= low && v <= high : v >= low && v < high),
		).length;
		if (count === 0) continue;
		const midpoint = (low + high) / 2;
		buckets.push({
			range: `${low.toFixed(0)} to ${high.toFixed(0)}`,
			count,
			midpoint,
		});
	}

	return buckets;
};

// ── Win Rate by Direction ────────────────────────────────────────────────────

export interface DirectionWinRateBar {
	direction: string;
	winRate: number;
	winRatePercent: number;
	wins: number;
	losses: number;
	total: number;
	netPL: number;
}

export const buildDirectionWinRateData = (
	tradeCases: TradeCaseInput[],
): DirectionWinRateBar[] => {
	const dirMap = new Map<
		string,
		{ wins: number; losses: number; total: number; netPL: number }
	>();

	for (const t of tradeCases) {
		if (!isTerminalExecuted(t)) continue;
		const plPercent = parseNumeric(t.profitLossPercent);
		const plMoney = parseNumeric(t.profitLossMoney);
		if (plPercent === null) continue;
		const dir = t.direction ?? "Unknown";

		const entry = dirMap.get(dir) ?? { wins: 0, losses: 0, total: 0, netPL: 0 };
		entry.total++;
		if (plPercent > 0) entry.wins++;
		else if (plPercent < 0) entry.losses++;
		if (plMoney !== null) entry.netPL += plMoney;
		dirMap.set(dir, entry);
	}

	return Array.from(dirMap.entries())
		.map(([direction, data]) => ({
			direction,
			winRate: data.total > 0 ? Number((data.wins / data.total).toFixed(3)) : 0,
			winRatePercent: data.total > 0 ? Number(((data.wins / data.total) * 100).toFixed(1)) : 0,
			wins: data.wins,
			losses: data.losses,
			total: data.total,
			netPL: Number(data.netPL.toFixed(2)),
		}))
		.sort((a, b) => b.total - a.total);
};

// ── Daily P/L Bar ────────────────────────────────────────────────────────────

export interface DailyPLBar {
	/** Date label, e.g. "Apr 10" */
	date: string;
	/** ISO date key for sorting, e.g. "2026-04-10" */
	dateKey: string;
	netPL: number;
	trades: number;
	wins: number;
	losses: number;
}

export const buildDailyPLData = (
	tradeCases: TradeCaseInput[],
): DailyPLBar[] => {
	const dayMap = new Map<
		string,
		{ netPL: number; trades: number; wins: number; losses: number }
	>();

	const monthNames = [
		"Jan", "Feb", "Mar", "Apr", "May", "Jun",
		"Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
	];

	for (const t of tradeCases) {
		if (!isTerminalExecuted(t) || t.closedAt === null) continue;
		const pl = parseNumeric(t.profitLossMoney);
		if (pl === null) continue;
		const plPercent = parseNumeric(t.profitLossPercent);

		const d = new Date(t.closedAt);
		const dateKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

		const entry = dayMap.get(dateKey) ?? { netPL: 0, trades: 0, wins: 0, losses: 0 };
		entry.netPL += pl;
		entry.trades++;
		if (plPercent !== null && plPercent > 0) entry.wins++;
		else if (plPercent !== null && plPercent < 0) entry.losses++;
		dayMap.set(dateKey, entry);
	}

	return Array.from(dayMap.entries())
		.map(([dateKey, data]) => {
			const parts = dateKey.split("-");
			const monthIdx = Number.parseInt(parts[1], 10) - 1;
			const day = Number.parseInt(parts[2], 10);
			return {
				date: `${monthNames[monthIdx]} ${day}`,
				dateKey,
				netPL: Number(data.netPL.toFixed(2)),
				trades: data.trades,
				wins: data.wins,
				losses: data.losses,
			};
		})
		.sort((a, b) => a.dateKey.localeCompare(b.dateKey));
};
